import { HugeiconsIcon } from "@hugeicons/react";
import {
  Alert01Icon,
  Analytics01Icon,
  ArrowDown01Icon,
  ArrowUp01Icon,
  ArrowUpRight01Icon,
  AtIcon,
  BankIcon,
  Building03Icon,
  Calendar03Icon,
  Call02Icon,
  Camera01Icon,
  Cancel01Icon,
  CheckmarkCircle01Icon,
  CheckmarkSquare02Icon,
  CircleIcon,
  Clock01Icon,
  Copy01Icon,
  Download01Icon,
  Facebook01Icon,
  Globe02Icon,
  Home01Icon,
  InformationCircleIcon,
  InstagramIcon,
  LanguageCircleIcon,
  LayoutGridIcon,
  Link01Icon,
  Linkedin01Icon,
  Location01Icon,
  LockIcon,
  Login01Icon,
  Mail01Icon,
  Menu01Icon,
  Message01Icon,
  Moon02Icon,
  Plant01Icon,
  PlusSignIcon,
  QrCodeIcon,
  RobotIcon,
  RulerIcon,
  Search01Icon,
  SentIcon,
  Settings01Icon,
  Shield01Icon,
  ShoppingCart01Icon,
  SnapchatIcon,
  StarHalfIcon,
  StarIcon,
  StarOffIcon,
  Sun03Icon,
  Target01Icon,
  TiktokIcon,
  TwitterIcon,
  Upload01Icon,
  UserCircleIcon,
  UserGroupIcon,
  UserIcon,
  Wallet01Icon,
  YoutubeIcon,
} from "@hugeicons-pro/core-solid-standard";
import type { CSSProperties } from "react";

type IconProps = {
  size?: number | string;
  color?: string;
  fill?: string;
  stroke?: number | string;
  withOutline?: boolean;
  className?: string;
  style?: CSSProperties;
};

const solid: Record<string, unknown> = {
  Search01Icon,
  Alert01Icon,
  ArrowUp01Icon,
  ArrowDown01Icon,
  ArrowUpRight01Icon,
  AtIcon,
  InstagramIcon,
  Linkedin01Icon,
  SnapchatIcon,
  TiktokIcon,
  TwitterIcon,
  YoutubeIcon,
  BankIcon,
  Building03Icon,
  Calendar03Icon,
  Camera01Icon,
  CheckmarkSquare02Icon,
  CheckmarkCircle01Icon,
  CircleIcon,
  Clock01Icon,
  Copy01Icon,
  Download01Icon,
  Link01Icon,
  Upload01Icon,
  Globe02Icon,
  Home01Icon,
  LanguageCircleIcon,
  LayoutGridIcon,
  LockIcon,
  Login01Icon,
  Mail01Icon,
  Location01Icon,
  Menu01Icon,
  Message01Icon,
  Moon02Icon,
  Call02Icon,
  Plant01Icon,
  PlusSignIcon,
  QrCodeIcon,
  RobotIcon,
  RulerIcon,
  SentIcon,
  Settings01Icon,
  Shield01Icon,
  ShoppingCart01Icon,
  StarIcon,
  StarHalfIcon,
  StarOffIcon,
  Sun03Icon,
  Target01Icon,
  UserIcon,
  UserCircleIcon,
  UserGroupIcon,
  Wallet01Icon,
  Cancel01Icon,
  Facebook01Icon,
  InformationCircleIcon,
  Analytics01Icon,
};

const fallbackIcon = Search01Icon as unknown;
const toNumber = (value: number | string | undefined, fallback: number) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number(value.replace(/[^0-9.]/g, ""));
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};
const defaultColor = "#6b7280";
const makeIcon = (candidates: string[]) => {
  const icon = (candidates.map((n) => solid[n]).find(Boolean) ?? fallbackIcon) as unknown;
  const IconComponent = ({ size = 20, color, fill, className, style }: IconProps) => (
    <HugeiconsIcon
      icon={icon as never}
      size={toNumber(size, 20)}
      color={color ?? fill ?? defaultColor}
      className={className}
      style={style}
    />
  );
  IconComponent.displayName = `HugeProIcon(${candidates[0] || "Fallback"})`;
  return IconComponent;
};

export const IconActivityHeartbeat = makeIcon(["Search01Icon"]);
export const IconAffiliate = makeIcon(["Search01Icon"]);
export const IconAlertCircle = makeIcon(["Alert01Icon"]);
export const IconApps = makeIcon(["Search01Icon"]);
export const IconArrowUp = makeIcon(["ArrowUp01Icon"]);
export const IconAt = makeIcon(["AtIcon"]);
export const IconBasketOff = makeIcon(["Search01Icon"]);
export const IconBathroomFilled = makeIcon(["Search01Icon"]);
export const IconBedFilled = makeIcon(["Search01Icon"]);
export const IconBrandInstagram = makeIcon(["InstagramIcon"]);
export const IconBrandLinkedin = makeIcon(["Linkedin01Icon"]);
export const IconBrandSnapchat = makeIcon(["SnapchatIcon"]);
export const IconBrandTiktok = makeIcon(["TiktokIcon"]);
export const IconBrandTwitter = makeIcon(["TwitterIcon"]);
export const IconBrandX = makeIcon(["TwitterIcon"]);
export const IconBrandYoutube = makeIcon(["YoutubeIcon"]);
export const IconBuildingBank = makeIcon(["BankIcon"]);
export const IconBuildings = makeIcon(["Building03Icon"]);
export const IconCalendar = makeIcon(["Calendar03Icon"]);
export const IconCamera = makeIcon(["Camera01Icon"]);
export const IconCheck = makeIcon(["CheckmarkSquare02Icon"]);
export const IconCheckbox = makeIcon(["CheckmarkSquare02Icon"]);
export const IconChevronDown = makeIcon(["ArrowDown01Icon"]);
export const IconCircleCheck = makeIcon(["CheckmarkCircle01Icon"]);
export const IconCircleFilled = makeIcon(["CircleIcon"]);
export const IconClock = makeIcon(["Clock01Icon"]);
export const IconCopy = makeIcon(["Copy01Icon"]);
export const IconDownload = makeIcon(["Download01Icon"]);
export const IconEdit = makeIcon(["Search01Icon"]);
export const IconExternalLink = makeIcon(["Link01Icon"]);
export const IconFileUpload = makeIcon(["Upload01Icon"]);
export const IconGlobe = makeIcon(["Globe02Icon"]);
export const IconHeart = makeIcon(["Search01Icon"]);
export const IconHeartFilled = makeIcon(["Search01Icon"]);
export const IconHouseFilled = makeIcon(["Home01Icon"]);
export const IconLanguage = makeIcon(["LanguageCircleIcon"]);
export const IconLayoutGrid = makeIcon(["LayoutGridIcon"]);
export const IconLock = makeIcon(["LockIcon"]);
export const IconLockFilled = makeIcon(["LockIcon"]);
export const IconLogout = makeIcon(["Login01Icon"]);
export const IconMail = makeIcon(["Mail01Icon"]);
export const IconMailFilled = makeIcon(["Mail01Icon"]);
export const IconMapPin = makeIcon(["Location01Icon"]);
export const IconMenu2 = makeIcon(["Menu01Icon"]);
export const IconMessage = makeIcon(["Message01Icon"]);
export const IconMessageCircle = makeIcon(["Message01Icon"]);
export const IconMessageDots = makeIcon(["Message01Icon"]);
export const IconMoon = makeIcon(["Moon02Icon"]);
export const IconPhone = makeIcon(["Call02Icon"]);
export const IconPhoneFilled = makeIcon(["Call02Icon"]);
export const IconPlant = makeIcon(["Plant01Icon"]);
export const IconPlugConnected = makeIcon(["Search01Icon"]);
export const IconPlugConnectedX = makeIcon(["Search01Icon"]);
export const IconPlus = makeIcon(["PlusSignIcon"]);
export const IconQRCode = makeIcon(["QrCodeIcon"]);
export const IconRobot = makeIcon(["RobotIcon"]);
export const IconRulerMeasureOutlined = makeIcon(["RulerIcon"]);
export const IconSearch = makeIcon(["Search01Icon"]);
export const IconSend = makeIcon(["SentIcon"]);
export const IconSettings = makeIcon(["Settings01Icon"]);
export const IconShield = makeIcon(["Shield01Icon"]);
export const IconShoppingCart = makeIcon(["ShoppingCart01Icon"]);
export const IconStar = makeIcon(["StarIcon"]);
export const IconStarFilled = makeIcon(["StarIcon"]);
export const IconStarHalf = makeIcon(["StarHalfIcon"]);
export const IconStarOff = makeIcon(["StarOffIcon"]);
export const IconSun = makeIcon(["Sun03Icon"]);
export const IconTarget = makeIcon(["Target01Icon"]);
export const IconTrendingUp = makeIcon(["ArrowUpRight01Icon"]);
export const IconUser = makeIcon(["UserIcon"]);
export const IconUserCircle = makeIcon(["UserCircleIcon"]);
export const IconUserFilled = makeIcon(["UserIcon"]);
export const IconUserOutlined = makeIcon(["UserCircleIcon"]);
export const IconUsers = makeIcon(["UserGroupIcon"]);
export const IconUsersGroup = makeIcon(["UserGroupIcon"]);
export const IconWallet = makeIcon(["Wallet01Icon"]);
export const IconWorld = makeIcon(["Globe02Icon"]);
export const IconWorldWww = makeIcon(["Globe02Icon"]);
export const IconX = makeIcon(["Cancel01Icon"]);
export const Iconrefresh = makeIcon(["Search01Icon"]);

export const IconBrandFacebook = makeIcon(["Facebook01Icon"]);
export const IconInfoCircle = makeIcon(["InformationCircleIcon"]);
export const IconChartHistogram = makeIcon(["Analytics01Icon"]);
